import {
  Menu,
  MenuButton,
  useMenuState,
  MenuItem,
  MenuGroup,
  MenuSeparator,
} from "@twilio-paste/menu";
import React, { useMemo, useState } from "react";
import { Client, ConnectionState, User } from "@twilio/conversations";
import UserProfileModal from "./modals/UserProfileModal";
import {
  getSwitchableLogins,
  readUserProfile,
  refreshLogin,
  saveLogin,
} from "../api";
import { formatPhoneNumber } from "../utils/displayName";
import CreateConversationButton from "./conversations/CreateConversationButton";

type AppHeaderProps = {
  user: string;
  onSignOut: () => void;
  connectionState: ConnectionState;
  client?: Client;
};
const AppHeader: React.FC<AppHeaderProps> = ({
  user,
  onSignOut,
  connectionState,
  client,
}) => {
  const menu = useMenuState();
  const switchableLogins = getSwitchableLogins();
  const [switchError, setSwitchError] = useState("");

  // Admins can text as any number: get a token for it and start over with it
  const switchTo = async (login: string) => {
    if (login === user) {
      return;
    }
    try {
      saveLogin(await refreshLogin(login));
      window.location.reload();
    } catch (e) {
      setSwitchError((e as Error).message);
    }
  };

  const [showUserProfileModal, setUserProfileModal] = useState(false);

  const [userProfile, setUserProfile] = useState<User | undefined>(undefined);

  const handleUserProfileModalClose = () => setUserProfileModal(false);

  const label: "online" | "connecting" | "offline" = useMemo(() => {
    switch (connectionState) {
      case "connected":
        return "online";
      case "connecting":
        return "connecting";
      default:
        return "offline";
    }
  }, [connectionState]);

  const handleUserProfileModalOpen = async () => {
    const userProfileTemp = await readUserProfile(user, client);
    setUserProfile(userProfileTemp);
    setUserProfileModal(true);
  };

  return (
    <div className="ios-list-bar">
      <MenuButton {...menu} variant="reset" size="reset">
        <span className="ios-me" title="Account">
          <span className={`ios-me-status ${label}`} />
          {formatPhoneNumber(user)}
        </span>
      </MenuButton>
      <Menu {...menu} aria-label="Preferences">
        {switchableLogins.length > 0 && (
          <>
            <MenuGroup label="Text as">
              {switchableLogins.map((login) => (
                <MenuItem {...menu} key={login} onClick={() => switchTo(login)}>
                  {login === user ? "✓ " : "\u2003"}
                  {formatPhoneNumber(login)}
                </MenuItem>
              ))}
            </MenuGroup>
            <MenuSeparator {...menu} />
          </>
        )}
        <MenuItem {...menu} onClick={handleUserProfileModalOpen}>
          User Profile
        </MenuItem>
        <MenuItem {...menu} onClick={onSignOut}>
          Sign Out
        </MenuItem>
      </Menu>
      <span className="ios-me-label" aria-live="polite">
        {switchError ||
          (label === "online"
            ? ""
            : label === "connecting"
            ? "Connecting…"
            : "Offline")}
      </span>
      <CreateConversationButton client={client} />
      {showUserProfileModal && (
        <UserProfileModal
          isModalOpen={showUserProfileModal}
          handleClose={handleUserProfileModalClose}
          user={userProfile}
        ></UserProfileModal>
      )}
    </div>
  );
};

export default AppHeader;
